import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Listing, ScenarioData, defaultListing } from '@/lib/index';

// Fields that stay independent per scenario (NOT synced from S1)
const PRICE_ONLY_FIELDS = new Set<keyof ScenarioData>(['salesPrice']);

// ── DB row shape ────────────────────────────────────────────────────────
interface ListingRow {
  id: string;
  seller_name: string;
  property: string;
  scenarios: ScenarioData[];
  created_at: string;
  updated_at: string;
}

function rowToListing(row: ListingRow): Listing {
  return {
    id: row.id,
    sellerName: row.seller_name,
    property: row.property,
    createdAt: row.created_at,
    scenarios: row.scenarios as [ScenarioData, ScenarioData, ScenarioData],
  };
}

// ── Debounce helper ─────────────────────────────────────────────────────
function useDebouncedCallback<T extends unknown[]>(fn: (...args: T) => void, delay: number) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback((...args: T) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

// ── Hook ────────────────────────────────────────────────────────────────
export function useListings() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Load all listings from Supabase on mount ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('listings')
          .select('*')
          .order('created_at', { ascending: true });

        if (err) throw err;
        if (cancelled) return;

        const loaded = (data as ListingRow[]).map(rowToListing);
        if (loaded.length === 0) {
          // No listings in DB yet — create a blank one
          const blank = defaultListing();
          await supabase.from('listings').insert({
            id: blank.id,
            seller_name: blank.sellerName,
            property: blank.property,
            scenarios: blank.scenarios,
          });
          setListings([blank]);
          setActiveId(blank.id);
        } else {
          setListings(loaded);
          setActiveId(loaded[0].id);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load listings. Check your connection.');
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const activeListing = listings.find(l => l.id === activeId) ?? listings[0];

  // ── Save a full listing to Supabase (debounced for rapid typing) ──────
  const _saveToDb = useCallback(async (listing: Listing) => {
    try {
      await supabase.from('listings').upsert({
        id: listing.id,
        seller_name: listing.sellerName,
        property: listing.property,
        scenarios: listing.scenarios,
      });
    } catch (e) {
      console.error('Save failed:', e);
    }
  }, []);

  const debouncedSave = useDebouncedCallback(_saveToDb, 700);

  // ── Create new listing ────────────────────────────────────────────────
  const createListing = useCallback(async () => {
    const newOne = defaultListing();
    try {
      await supabase.from('listings').insert({
        id: newOne.id,
        seller_name: newOne.sellerName,
        property: newOne.property,
        scenarios: newOne.scenarios,
      });
      setListings(prev => [...prev, newOne]);
      setActiveId(newOne.id);
    } catch (e) {
      console.error('Create listing failed:', e);
    }
  }, []);

  // ── Delete listing ────────────────────────────────────────────────────
  const deleteListing = useCallback(async (id: string) => {
    try {
      await supabase.from('listings').delete().eq('id', id);
      setListings(prev => {
        const updated = prev.filter(l => l.id !== id);
        if (updated.length === 0) {
          // All deleted — create a fresh one async
          createListing();
          return prev;
        }
        setActiveId(curr => curr === id ? updated[0].id : curr);
        return updated;
      });
    } catch (e) {
      console.error('Delete listing failed:', e);
    }
  }, [createListing]);

  // ── Update seller name / property ─────────────────────────────────────
  const updateListingMeta = useCallback((
    id: string,
    meta: Partial<Pick<Listing, 'sellerName' | 'property'>>,
  ) => {
    setListings(prev => {
      const updated = prev.map(l => l.id === id ? { ...l, ...meta } : l);
      const listing = updated.find(l => l.id === id);
      if (listing) debouncedSave(listing);
      return updated;
    });
  }, [debouncedSave]);

  // ── Update a scenario field ───────────────────────────────────────────
  // Rule: editing Scenario 1 mirrors all non-price fields to S2 & S3
  const updateScenario = useCallback((
    listingId: string,
    scenarioIdx: 0 | 1 | 2,
    field: keyof ScenarioData,
    value: number | string,
  ) => {
    setListings(prev => {
      const updated = prev.map(l => {
        if (l.id !== listingId) return l;
        const scenarios = [...l.scenarios] as [ScenarioData, ScenarioData, ScenarioData];
        scenarios[scenarioIdx] = { ...scenarios[scenarioIdx], [field]: value };
        // Mirror S1 → S2/S3 for non-price fields
        if (scenarioIdx === 0 && !PRICE_ONLY_FIELDS.has(field)) {
          scenarios[1] = { ...scenarios[1], [field]: value };
          scenarios[2] = { ...scenarios[2], [field]: value };
        }
        return { ...l, scenarios };
      });
      const listing = updated.find(l => l.id === listingId);
      if (listing) debouncedSave(listing);
      return updated;
    });
  }, [debouncedSave]);

  return {
    listings,
    activeListing,
    activeId,
    setActiveId,
    loading,
    error,
    createListing,
    deleteListing,
    updateListingMeta,
    updateScenario,
  };
}
